import { useTheme } from "../../hooks/useTheme";
import { useCallback, useMemo, useState } from "react";
import { Colors } from "../../utils/colors";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import {
  FormCheckBoxField,
  FormRadioField,
  FormAnswersV2,
  FormInputType,
  SavedFormQuestion,
  ThemeType,
} from "@markit/common.types";
import { Icon } from "@iconify/react";
import checkbox from "@iconify/icons-mdi/checkbox-outline";
import CustomLinkify from "../Links/CustomLinkify";
import { changeFormAnswerV2 } from "@markit/common.utils";
import CustomTextField from "../../components/CustomTextField";
import MediaUploadFile from "../Media/MediaUploadFile";
import { useFormResponsesContext } from "../Form/FormResponsesContext";

type SingularQuestionInputProps = {
  question: SavedFormQuestion;
  formQuestions: SavedFormQuestion[];
  answers: FormAnswersV2;
  setAnswers: (newAnswers: FormAnswersV2) => void;
  disabled: boolean;
  themeType?: ThemeType;
};

/**
 * To be used for text fields with the verified text in the right of the text box
 */
const SingularQuestionInput = (props: SingularQuestionInputProps) => {
  const { question, formQuestions, answers, setAnswers, disabled, themeType } =
    props;
  const { theme } = useTheme(themeType);
  const { formFileUploads, setFormFileUploads, formErrorIds, setFormErrorIds } =
    useFormResponsesContext();
  const [otherOptionSelected, setOtherOptionSelected] = useState(false);

  const styles = {
    inputContainer: {
      alignSelf: undefined,
      width: undefined,
      height: "auto",
    },
  };

  const answerInputArr = useMemo(
    () => answers[question.id] ?? [],
    [answers, question.id]
  );

  const getOptionMeta = useMemo(() => {
    const input = question.formInput as FormCheckBoxField | FormRadioField;
    const inactiveOptions = input.inactiveOptions || [];
    return {
      hasOtherOption: Boolean(input.hasOtherOption),
      options: input.options || [],
      inactiveOptions: inactiveOptions,
    };
  }, [question.formInput]);

  const handleChange = useCallback(
    (questionId: string, answer: string[]) => {
      if (formErrorIds.includes(questionId))
        setFormErrorIds(formErrorIds.filter((id) => id !== questionId));
      const newFormAnswers = changeFormAnswerV2({
        answers: answers,
        questions: formQuestions,
        questionId: questionId,
        answer: answer,
      });
      setAnswers(newFormAnswers);
    },
    [answers, formErrorIds, formQuestions, setAnswers, setFormErrorIds]
  );

  const checkboxElement = useCallback(
    (isChecked: boolean, onChange: (event: any) => void, isError: boolean) => (
      <Checkbox
        checked={isChecked}
        onChange={onChange}
        inputProps={{ color: theme.PrimaryText.color }}
        style={{ paddingLeft: 0 }}
        color="default"
        sx={
          isError ? { color: Colors.RED1 } : { color: theme.PrimaryText.color }
        }
        checkedIcon={<Icon icon={checkbox} height={24} />}
        disabled={disabled}
      />
    ),
    [disabled, theme.PrimaryText.color]
  );

  const getElement = useMemo(() => {
    const isError = formErrorIds.includes(question.id);

    switch (question.formInput.inputType) {
      case FormInputType.TEXT_INPUT: {
        return (
          <CustomTextField
            value={answerInputArr[0]}
            placeholder={question.formInput.placeholder || "Answer here..."}
            disabled={disabled}
            inputMode="text"
            borderRadius={12}
            onChange={(event: any) => {
              handleChange(question.id, [event.target.value]);
            }}
            error={isError}
            eventTheme={themeType}
            containerStyles={styles.inputContainer}
          />
        );
      }
      case FormInputType.EMAIL: {
        return (
          <CustomTextField
            value={answerInputArr[0]}
            placeholder={question.formInput.placeholder || "Answer here..."}
            disabled={disabled}
            inputMode="email"
            borderRadius={12}
            onChange={(event: any) => {
              handleChange(question.id, [event.target.value]);
            }}
            error={isError}
            eventTheme={themeType}
            containerStyles={styles.inputContainer}
          />
        );
      }
      case FormInputType.CHECK_BOX: {
        const selectedListedOptions = answerInputArr.filter((answer) =>
          getOptionMeta.options.includes(answer)
        );
        const hasCustomOther = answerInputArr.some(
          (a) => !getOptionMeta.options.includes(a)
        );
        const otherRowChecked = otherOptionSelected || hasCustomOther;
        const otherResponse =
          answerInputArr.find(
            (answer) => !getOptionMeta.options.includes(answer)
          ) || "";
        return (
          <>
            {getOptionMeta.options.map((option, index) => (
              <FormControlLabel
                key={index}
                sx={{ marginLeft: 0 }}
                control={checkboxElement(
                  selectedListedOptions.includes(option),
                  (event: any) => {
                    const listedOnly = answerInputArr.filter((a) =>
                      getOptionMeta.options.includes(a)
                    );
                    const otherTexts = answerInputArr.filter(
                      (a) => !getOptionMeta.options.includes(a)
                    );
                    const nextListed = event.target.checked
                      ? [...listedOnly, option]
                      : listedOnly.filter((val) => val !== option);
                    handleChange(question.id, [...nextListed, ...otherTexts]);
                  },
                  isError
                )}
                label={
                  <span className="bodyRegular" style={theme.PrimaryText}>
                    {option}
                  </span>
                }
              />
            ))}
            {getOptionMeta.hasOtherOption ? (
              <FormControlLabel
                key={"other_checkbox"}
                sx={{
                  marginLeft: 0,
                  "& .MuiFormControlLabel-label": { flex: 1, minWidth: 0 },
                }}
                control={checkboxElement(
                  otherRowChecked,
                  (event: any) => {
                    const checked = event.target.checked;
                    if (!checked) {
                      setOtherOptionSelected(false);
                      handleChange(
                        question.id,
                        answerInputArr.filter((a) =>
                          getOptionMeta.options.includes(a)
                        )
                      );
                    } else {
                      setOtherOptionSelected(true);
                    }
                  },
                  isError
                )}
                label={
                  otherRowChecked ? (
                    <CustomTextField
                      value={otherResponse}
                      placeholder={"Other..."}
                      disabled={disabled}
                      onChange={(event: any) => {
                        const val = event.target.value;
                        const listedOnly = answerInputArr.filter((a) =>
                          getOptionMeta.options.includes(a)
                        );
                        handleChange(
                          question.id,
                          val ? [...listedOnly, val] : listedOnly
                        );
                      }}
                      inputMode="text"
                      eventTheme={themeType}
                    />
                  ) : (
                    <span className="bodyRegular" style={theme.PrimaryText}>
                      Other
                    </span>
                  )
                }
              />
            ) : null}
          </>
        );
      }
      case FormInputType.RADIO: {
        const selectedValue = answerInputArr[0] || "";
        const isOtherSelected =
          selectedValue !== "" &&
          !getOptionMeta.options.includes(selectedValue) &&
          !getOptionMeta.inactiveOptions.includes(selectedValue);
        const otherRowChecked = otherOptionSelected || isOtherSelected;

        const selectedListedValue = otherRowChecked
          ? "other_radio"
          : getOptionMeta.options.includes(selectedValue)
            ? selectedValue
            : "";
        const otherResponse = otherRowChecked ? selectedValue : "";
        return (
          <RadioGroup
            value={selectedListedValue}
            onChange={(event: any) => {
              const value = event.target.value;
              handleChange(question.id, value === "other_radio" ? [] : [value]);
              setOtherOptionSelected(value === "other_radio");
            }}
          >
            {getOptionMeta.options.map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                onClick={() => {
                  // Handle clearing if selecting the same option
                  if (option === selectedValue && !isOtherSelected) {
                    handleChange(question.id, [option]);
                  }
                }}
                control={
                  <Radio
                    color="default"
                    sx={{
                      color: isError ? Colors.RED1 : theme.PrimaryText.color,
                      "&.Mui-disabled": {
                        color: theme.PrimaryText.color,
                      },
                    }}
                  />
                }
                label={
                  <span className="bodyRegular" style={theme.PrimaryText}>
                    {option}
                  </span>
                }
                disabled={disabled}
              />
            ))}
            {getOptionMeta.hasOtherOption ? (
              <FormControlLabel
                key={"other_radio"}
                value={"other_radio"}
                sx={{
                  width: "100%",
                  "& .MuiFormControlLabel-label": { flex: 1, minWidth: 0 },
                }}
                control={
                  <Radio
                    color="default"
                    sx={{
                      color: isError ? Colors.RED1 : theme.PrimaryText.color,
                      "&.Mui-disabled": {
                        color: theme.PrimaryText.color,
                      },
                    }}
                  />
                }
                label={
                  otherRowChecked ? (
                    <CustomTextField
                      value={otherResponse}
                      placeholder={"Other..."}
                      disabled={disabled}
                      onChange={(event: any) => {
                        const val = event.target.value;
                        handleChange(question.id, val ? [val] : []);
                      }}
                      inputMode="text"
                      eventTheme={themeType}
                    />
                  ) : (
                    <span className="bodyRegular" style={theme.PrimaryText}>
                      Other
                    </span>
                  )
                }
                disabled={disabled}
              />
            ) : null}
          </RadioGroup>
        );
      }
      case FormInputType.FILE_UPLOAD: {
        const fileAnswer = answerInputArr[0] || "";
        const displayName = formFileUploads[question.id] || "";
        return (
          <div style={{ paddingTop: 7 }}>
            {fileAnswer ? (
              <div
                className="AlignedRow"
                style={{
                  padding: "12px 14px",
                  borderRadius: 12,
                  border: `1px solid ${theme.EditableTextFieldBorder.borderColor}`,
                  gap: 4,
                  width: "fit-content",
                }}
              >
                <div className="AlignedRow" style={{ gap: 4 }}>
                  <div>
                    <Icon
                      icon="ion:image"
                      height={14}
                      style={{ color: theme.PrimaryText.color }}
                    />
                  </div>
                  <span
                    className="bodySmallMedium OneLineText"
                    style={{ ...theme.PrimaryText }}
                  >
                    {displayName || "file"}
                  </span>
                </div>
                <div>
                  <Icon
                    icon="ion:close"
                    height={16}
                    style={{
                      color: theme.PrimaryText.color,
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      handleChange(question.id, []);
                      setFormFileUploads((prev) => {
                        const n = { ...prev };
                        delete n[question.id];
                        return n;
                      });
                    }}
                  />
                </div>
              </div>
            ) : (
              <MediaUploadFile
                supportedFileTypes={["PNG", "JPG", "JPEG", "PDF"]}
                onUpload={(blobUrl, fileName) => {
                  const name = fileName || "file";
                  setFormFileUploads((prev) => ({
                    ...prev,
                    [question.id]: name,
                  }));
                  handleChange(question.id, [blobUrl]);
                }}
                theme={theme}
                disabled={disabled}
                containerStyles={{
                  borderColor: isError ? Colors.RED3 : Colors.GRAY2,
                }}
              />
            )}
          </div>
        );
      }
      default:
        return null;
    }
  }, [
    formErrorIds,
    question.id,
    question.formInput,
    answerInputArr,
    disabled,
    themeType,
    styles.inputContainer,
    handleChange,
    getOptionMeta.options,
    getOptionMeta.hasOtherOption,
    checkboxElement,
    otherOptionSelected,
    theme,
    getOptionMeta.inactiveOptions,
    formFileUploads,
    setFormFileUploads,
  ]);

  return (
    <div key={question.id} style={{ opacity: disabled ? 0.3 : 1 }}>
      <FormControl
        style={{ width: "100%" }}
        error={formErrorIds.includes(question.id)}
      >
        {question.formInput.label !== undefined &&
        question.formInput.label !== "" ? (
          <FormLabel
            id="demo-radio-buttons-group-label"
            required={question.formInput.isRequired}
            style={{ ...theme.PrimaryText, paddingBottom: 5 }}
          >
            <CustomLinkify>
              <span
                className="bodyMedium"
                style={{ ...theme.PrimaryText, fontWeight: 600 }}
              >
                {question.formInput.label}
              </span>
            </CustomLinkify>
          </FormLabel>
        ) : undefined}
        {getElement}
      </FormControl>
    </div>
  );
};

export default SingularQuestionInput;
